import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  PackagePlus, Package, Search, Loader2, X, Plus, RefreshCw, CheckCircle2,
  Truck, CalendarDays, XCircle, ClipboardList, Undo2, TrendingUp,
} from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';

// Phase 3 · 입고관리 (입고예정/부분입고/입고완료/취소)

const STATUS_LABEL = { expected: '입고예정', partial: '부분입고', completed: '입고완료', cancelled: '취소' };
const STATUS_CLS = {
  expected:  'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300',
  partial:   'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/10 dark:border-indigo-500/30 dark:text-indigo-300',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-white/5 dark:border-white/10',
};
const fmt = (d) => { if (!d) return '-'; try { return format(new Date(d), 'yyyy.MM.dd HH:mm', { locale: ko }); } catch { return '-'; } };

export default function InboundSection() {
  const [tab, setTab] = useState('open');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState(null);   // inbound row
  const [waitingTarget, setWaitingTarget] = useState(null);   // { product_id, variant_key, product_name }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab && tab !== 'all') params.status = tab;
      if (q) params.q = q;
      const r = await api.get('/seller/inbound', { params });
      setRows(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } catch (e) { toast.error(e?.response?.data?.detail || '불러오기 실패'); }
    finally { setLoading(false); }
  }, [tab, q]);
  useEffect(() => { load(); }, [load]);

  const cancel = async (row) => {
    if (!window.confirm(`'${row.product_name_snapshot}' 입고예정을 취소하시겠습니까?`)) return;
    try {
      await api.post(`/seller/inbound/${row.inbound_id}/cancel`, { reason: '판매자 취소' });
      toast.success('취소되었습니다.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };

  return (
    <div className="space-y-4" data-testid="seller-inbound-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>입고관리</h2>
          <p className="text-xs text-gray-500 mt-1">
            상품별 입고예정을 등록하고 실제 입고 시 재고에 반영합니다. 실 WMS/공급처 API 미연동 (판매자 수동 입력).
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setOpenCreate(true)} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#FF3B30] text-white text-xs font-black" data-testid="inb-open-create">
            <Plus size={12}/> 입고예정 등록
          </button>
          <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs font-bold hover:bg-black/5 dark:hover:bg-white/5" data-testid="inb-refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''}/> 새로고침
          </button>
        </div>
      </div>

      {/* Summary + Tabs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['expected', 'partial', 'completed', 'cancelled'].map((k) => (
          <button key={k} onClick={() => setTab(k)}
            className={`rounded-2xl border p-3 text-left transition-colors ${tab === k ? STATUS_CLS[k] + ' border-2' : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5'}`}
            data-testid={`inb-sum-${k}`}>
            <p className="text-[10px] font-black uppercase tracking-wider">{STATUS_LABEL[k]}</p>
            <p className="text-2xl font-black mt-1">{counts?.[k] || 0}</p>
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3 space-y-2">
        <div className="flex flex-wrap gap-1" data-testid="inb-status-tabs">
          {['open', 'all', 'expected', 'partial', 'completed', 'cancelled'].map((k) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`inb-tab-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-black border ${tab === k ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'}`}>
              {k === 'open' ? '진행중 (예정+부분)' : (k === 'all' ? '전체' : STATUS_LABEL[k])}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-3 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="상품명 · SKU · 공급처 · 메모"
            className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
            data-testid="inb-search"/>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-gray-400"><Loader2 className="animate-spin inline mr-1" size={14}/> 불러오는 중…</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="inb-empty">
          <PackagePlus className="mx-auto text-gray-300" size={28} />
          <p className="mt-3 text-sm font-bold">해당 조건의 입고예정이 없습니다.</p>
        </div>
      ) : (
        <>
          {/* PC table */}
          <div className="hidden md:block overflow-x-auto rounded-2xl border border-black/10 dark:border-white/10" data-testid="inb-table">
            <table className="w-full text-xs">
              <thead className="bg-black/[0.03] dark:bg-white/5">
                <tr className="text-left">
                  <th className="px-2 py-2 font-black">상품 / SKU / 옵션</th>
                  <th className="px-2 py-2 font-black">공급처</th>
                  <th className="px-2 py-2 font-black">예정 수량</th>
                  <th className="px-2 py-2 font-black">입고 완료</th>
                  <th className="px-2 py-2 font-black">잔여</th>
                  <th className="px-2 py-2 font-black">예정일</th>
                  <th className="px-2 py-2 font-black">상태</th>
                  <th className="px-2 py-2 font-black">액션</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.inbound_id} className="border-t border-black/5 dark:border-white/10" data-testid={`inb-row-${r.inbound_id}`}>
                    <td className="px-2 py-2">
                      <p className="font-bold truncate">{r.product_name_snapshot}</p>
                      <p className="text-[10px] text-gray-500 font-mono">{r.sku || '-'} · {r.variant_key !== 'default' ? r.variant_key : '기본옵션'}</p>
                    </td>
                    <td className="px-2 py-2">{r.supplier || '-'}</td>
                    <td className="px-2 py-2 font-mono">{r.expected_qty}</td>
                    <td className="px-2 py-2 font-mono text-emerald-600">{r.received_qty || 0}</td>
                    <td className="px-2 py-2 font-mono text-[#FF3B30]" data-testid={`inb-remaining-${r.inbound_id}`}>{r.remaining_qty}</td>
                    <td className="px-2 py-2">{r.expected_date || '-'}</td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-black ${STATUS_CLS[r.status]}`} data-testid={`inb-status-${r.inbound_id}`}>
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(r.status === 'expected' || r.status === 'partial') && (
                          <>
                            <button onClick={() => setReceiveTarget(r)} className="px-2 py-1 rounded bg-emerald-600 text-white text-[11px] font-black" data-testid={`inb-receive-${r.inbound_id}`}>
                              <Truck size={10} className="inline mr-0.5"/>입고 처리
                            </button>
                            <button onClick={() => cancel(r)} className="px-2 py-1 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold" data-testid={`inb-cancel-${r.inbound_id}`}>취소</button>
                          </>
                        )}
                        <button onClick={() => setWaitingTarget({ product_id: r.product_id, variant_key: r.variant_key, product_name: r.product_name_snapshot })}
                          className="px-2 py-1 rounded border border-indigo-300 text-indigo-700 dark:border-indigo-500/30 dark:text-indigo-300 text-[11px] font-bold"
                          data-testid={`inb-waiting-${r.inbound_id}`}>
                          대기 주문
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden space-y-2" data-testid="inb-cards">
            {rows.map((r) => (
              <div key={r.inbound_id} className="bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 p-3" data-testid={`inb-card-${r.inbound_id}`}>
                <div className="flex justify-between items-start mb-1">
                  <p className="font-bold text-sm truncate">{r.product_name_snapshot}</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-black ${STATUS_CLS[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                </div>
                <p className="text-[10px] text-gray-500 font-mono">{r.sku || '-'} · {r.variant_key !== 'default' ? r.variant_key : '기본옵션'}</p>
                <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
                  <div><span className="text-gray-500">예정</span> <b className="ml-1">{r.expected_qty}</b></div>
                  <div><span className="text-gray-500">입고</span> <b className="ml-1 text-emerald-600">{r.received_qty || 0}</b></div>
                  <div><span className="text-gray-500">잔여</span> <b className="ml-1 text-[#FF3B30]">{r.remaining_qty}</b></div>
                </div>
                {r.expected_date && <p className="text-[11px] text-gray-500 mt-1">예정일: {r.expected_date}</p>}
                {r.supplier && <p className="text-[11px] text-gray-500">공급처: {r.supplier}</p>}
                <div className="mt-2 flex flex-wrap gap-1">
                  {(r.status === 'expected' || r.status === 'partial') && (
                    <>
                      <button onClick={() => setReceiveTarget(r)} className="flex-1 px-2 py-1.5 rounded bg-emerald-600 text-white text-[11px] font-black">입고 처리</button>
                      <button onClick={() => cancel(r)} className="px-3 py-1.5 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold">취소</button>
                    </>
                  )}
                  <button onClick={() => setWaitingTarget({ product_id: r.product_id, variant_key: r.variant_key, product_name: r.product_name_snapshot })}
                    className="px-3 py-1.5 rounded border border-indigo-300 text-indigo-700 text-[11px] font-bold">대기 주문</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {openCreate && <CreateInboundModal onClose={() => setOpenCreate(false)} onCreated={() => { setOpenCreate(false); load(); }} />}
      {receiveTarget && <ReceiveInboundModal inbound={receiveTarget} onClose={() => setReceiveTarget(null)} onDone={() => { setReceiveTarget(null); load(); }} />}
      {waitingTarget && <WaitingOrdersModal target={waitingTarget} onClose={() => setWaitingTarget(null)} onSoldOut={() => { setWaitingTarget(null); load(); }} />}
    </div>
  );
}

function CreateInboundModal({ onClose, onCreated }) {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState('');
  const [variants, setVariants] = useState([]);
  const [variantKey, setVariantKey] = useState('default');
  const [expectedQty, setExpectedQty] = useState(0);
  const [expectedDate, setExpectedDate] = useState('');
  const [supplier, setSupplier] = useState('');
  const [memo, setMemo] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/seller/inventory', { params: { limit: 500 } });
        const items = r.data?.items || [];
        setVariants(items);
        // Build product list (dedup)
        const seen = new Set(); const ps = [];
        items.forEach((it) => {
          if (!seen.has(it.product_id)) { seen.add(it.product_id); ps.push({ product_id: it.product_id, name: it.product_name || it.product_name_snapshot }); }
        });
        setProducts(ps);
      } catch (e) { toast.error('상품 목록 로드 실패'); }
    })();
  }, []);

  const filteredVariants = variants.filter((v) => v.product_id === productId);

  const submit = async () => {
    if (!productId) { toast.error('상품을 선택해주세요.'); return; }
    if (!expectedQty || Number(expectedQty) <= 0) { toast.error('예정 수량은 1 이상이어야 합니다.'); return; }
    setBusy(true);
    try {
      const v = variants.find((x) => x.product_id === productId && x.variant_key === variantKey);
      await api.post('/seller/inbound', {
        product_id: productId,
        variant_key: variantKey,
        option: v?.option_snapshot || {},
        expected_qty: Number(expectedQty),
        expected_date: expectedDate || null,
        supplier: supplier || null,
        memo: memo || null,
      });
      toast.success('입고예정이 등록되었습니다.');
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="inb-create-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <div className="relative bg-white dark:bg-[#0A0A0A] rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-black/10 dark:border-white/10 flex justify-between items-center">
          <p className="text-sm font-black flex items-center gap-1"><PackagePlus size={14}/> 입고예정 등록</p>
          <button onClick={onClose}><X size={14}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <p className="text-[10px] font-black uppercase text-gray-500 mb-1">상품 *</p>
            <select value={productId} onChange={(e) => { setProductId(e.target.value); setVariantKey('default'); }}
              className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="inb-create-product">
              <option value="">상품 선택</option>
              {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
            </select>
          </div>
          {filteredVariants.length > 1 && (
            <div>
              <p className="text-[10px] font-black uppercase text-gray-500 mb-1">옵션 *</p>
              <select value={variantKey} onChange={(e) => setVariantKey(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="inb-create-variant">
                {filteredVariants.map((v) => (
                  <option key={v.variant_key} value={v.variant_key}>
                    {v.variant_key === 'default' ? '기본옵션' : v.variant_key} · SKU {v.sku || '-'} · 현재재고 {v.total_stock}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <p className="text-[10px] font-black uppercase text-gray-500 mb-1">입고 예정 수량 *</p>
            <input type="number" min={1} value={expectedQty} onChange={(e) => setExpectedQty(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent font-mono" data-testid="inb-create-qty"/>
          </div>
          <div>
            <p className="text-[10px] font-black uppercase text-gray-500 mb-1">입고 예정일</p>
            <input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="inb-create-date"/>
          </div>
          <div>
            <p className="text-[10px] font-black uppercase text-gray-500 mb-1">공급처 (선택)</p>
            <input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="예: ㈜공급처명"
              className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="inb-create-supplier"/>
          </div>
          <div>
            <p className="text-[10px] font-black uppercase text-gray-500 mb-1">메모</p>
            <textarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={2}
              className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="inb-create-memo"/>
          </div>
          <button onClick={submit} disabled={busy} className="w-full py-2.5 rounded-full bg-[#FF3B30] text-white text-sm font-black disabled:opacity-40" data-testid="inb-create-submit">
            {busy ? '등록 중…' : '입고예정 등록'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReceiveInboundModal({ inbound, onClose, onDone }) {
  const remaining = Math.max(0, (inbound.expected_qty || 0) - (inbound.received_qty || 0));
  const [qty, setQty] = useState(remaining);
  const [memo, setMemo] = useState('');
  const [busy, setBusy] = useState(false);
  const [released, setReleased] = useState(null);

  const submit = async () => {
    const n = Number(qty);
    if (!n || n <= 0) { toast.error('입고 수량을 입력해주세요.'); return; }
    if (n > remaining) { toast.error(`잔여 예정 ${remaining}개를 초과할 수 없습니다.`); return; }
    setBusy(true);
    try {
      const r = await api.post(`/seller/inbound/${inbound.inbound_id}/receive`, { qty: n, memo: memo || null });
      toast.success(`${n}개 입고 처리 완료 · 재고 반영됨`);
      setReleased(r.data?.released_orders || []);
      // Notify user of released orders
      if ((r.data?.released_count || 0) > 0) {
        toast.success(`대기 주문 ${r.data.released_count}건이 출고준비로 자동 전환되었습니다.`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="inb-receive-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <div className="relative bg-white dark:bg-[#0A0A0A] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-black/10 dark:border-white/10 flex justify-between items-center">
          <p className="text-sm font-black flex items-center gap-1"><Truck size={14}/> 입고 처리</p>
          <button onClick={onClose}><X size={14}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="rounded-lg bg-black/[0.03] dark:bg-white/[0.03] p-2 text-xs">
            <p className="font-bold">{inbound.product_name_snapshot}</p>
            <p className="text-[10px] text-gray-500 font-mono">{inbound.sku || '-'} · {inbound.variant_key !== 'default' ? inbound.variant_key : '기본옵션'}</p>
            <div className="grid grid-cols-3 gap-1 mt-2 text-[11px]">
              <div><span className="text-gray-500">예정</span> <b>{inbound.expected_qty}</b></div>
              <div><span className="text-gray-500">입고완료</span> <b className="text-emerald-600">{inbound.received_qty || 0}</b></div>
              <div><span className="text-gray-500">잔여</span> <b className="text-[#FF3B30]">{remaining}</b></div>
            </div>
          </div>
          {!released && (
            <>
              <div>
                <p className="text-[10px] font-black uppercase text-gray-500 mb-1">이번 입고 수량 (최대 {remaining})</p>
                <input type="number" min={1} max={remaining} value={qty} onChange={(e) => setQty(e.target.value)}
                  className="w-full px-3 py-2 text-lg font-mono rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="inb-receive-qty"/>
                <div className="flex gap-1 mt-1">
                  <button onClick={() => setQty(remaining)} className="text-[10px] text-[#FF3B30] font-bold" data-testid="inb-receive-full">전량 입고</button>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase text-gray-500 mb-1">메모</p>
                <input value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="선택"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="inb-receive-memo"/>
              </div>
              <button onClick={submit} disabled={busy || !qty || Number(qty) <= 0} className="w-full py-3 rounded-full bg-emerald-600 text-white text-sm font-black disabled:opacity-40" data-testid="inb-receive-submit">
                {busy ? '처리 중…' : `${qty || 0}개 입고 처리`}
              </button>
            </>
          )}
          {released && (
            <div className="space-y-2" data-testid="inb-receive-result">
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 p-2 text-xs text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 size={12} className="inline mr-1"/> 입고 처리가 완료되었습니다.
              </div>
              {released.length > 0 ? (
                <>
                  <p className="text-[10px] font-black uppercase text-gray-500">대기 주문 → 출고준비 전환</p>
                  {released.map((r) => (
                    <div key={r.seller_order_id} className="p-2 rounded border border-indigo-200 dark:border-indigo-500/30 text-[11px]" data-testid={`inb-released-${r.seller_order_id}`}>
                      <p className="font-mono font-bold">{r.order_number}</p>
                      <p className="text-gray-500">{r.released_qty}개 대기 해제 · 상태 <b className="text-indigo-700 dark:text-indigo-300">{r.new_status}</b></p>
                    </div>
                  ))}
                </>
              ) : <p className="text-xs text-gray-500">이 SKU 를 기다리던 주문이 없습니다.</p>}
              <button onClick={onDone} className="w-full py-2 rounded-full bg-black text-white dark:bg-white dark:text-black text-xs font-black">확인</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WaitingOrdersModal({ target, onClose, onSoldOut }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/seller/inventory/waiting-orders', {
          params: { product_id: target.product_id, variant_key: target.variant_key },
        });
        setRows(r.data?.items || []);
      } catch (e) { toast.error('불러오기 실패'); }
      finally { setLoading(false); }
    })();
  }, [target]);

  const markSoldOut = async () => {
    if (!window.confirm(`'${target.product_name}' 을(를) 품절 확정 처리하시겠습니까?\n대기중 주문은 '품절' 상태로 표시되며, 관련 입고예정도 함께 취소됩니다.`)) return;
    setBusy(true);
    try {
      const r = await api.post('/seller/inventory/mark-sold-out', {
        product_id: target.product_id, variant_key: target.variant_key,
      });
      toast.success(`품절 확정 완료 · ${r.data.affected_count}건 주문 표시됨`);
      onSoldOut();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="inb-waiting-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <div className="relative bg-white dark:bg-[#0A0A0A] rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-black/10 dark:border-white/10 flex justify-between items-center">
          <div>
            <p className="text-sm font-black flex items-center gap-1"><ClipboardList size={14}/> 대기 주문 조회</p>
            <p className="text-[11px] text-gray-500 truncate">{target.product_name}</p>
          </div>
          <button onClick={onClose}><X size={14}/></button>
        </div>
        <div className="p-4 space-y-3">
          {loading ? (
            <div className="py-8 text-center text-sm text-gray-400"><Loader2 size={14} className="animate-spin inline"/> 불러오는 중…</div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">이 SKU 를 기다리는 주문이 없습니다.</p>
          ) : (
            <div className="space-y-2" data-testid="inb-waiting-list">
              {rows.map((r) => (
                <div key={r.seller_order_id} className="p-2 rounded border border-amber-200 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-500/5 text-[11px]" data-testid={`inb-waiting-row-${r.seller_order_id}`}>
                  <p className="font-mono font-black">{r.order_number} · {r.buyer_name || '-'}</p>
                  <p className="text-gray-500">잔여 {r.pending_qty}개 · {r.buyer_phone || '-'} · {r.eta_date && `ETA ${r.eta_date}`}</p>
                </div>
              ))}
            </div>
          )}
          <button onClick={markSoldOut} disabled={busy} className="w-full py-2.5 rounded-full bg-rose-600 text-white text-sm font-black disabled:opacity-40" data-testid="inb-mark-soldout">
            <XCircle size={12} className="inline mr-1"/> 품절 확정 처리 (해당 SKU 입고예정도 함께 취소)
          </button>
          <p className="text-[10px] text-gray-500 text-center">품절 확정 시 대기 주문은 자동 취소되지 않고 '품절' 상태로 표시됩니다. 이후 판매자가 개별 취소/환불/RMA 처리를 결정하세요.</p>
        </div>
      </div>
    </div>
  );
}
